import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { getAuthHeader } from "../lib/auth";

function apiUrl(path) {
  const base = String(api.base || "/api").replace(/\/$/, "");
  const suffix = String(path || "").startsWith("/") ? path : `/${path}`;
  return `${base}${suffix}`;
}

function formatTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

export default function QuestionResponse() {
  const { questionId } = useParams();
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(
          apiUrl(`/tournaments/questions/${encodeURIComponent(questionId || "")}`),
          {
            headers: { Accept: "application/json", Authorization: getAuthHeader() },
            mode: "cors",
            credentials: "omit",
          }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.message || data?.error || "Could not load this response.");
        if (!cancelled) setQuestion(data?.question || null);
      } catch (loadError) {
        if (!cancelled) setError(loadError?.message || "Could not load this response.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [questionId]);

  return (
    <div className="page" style={styles.page}>
      <section style={styles.hero}>
        <div style={styles.eyebrow}>GAStreaming RESPONSE</div>
        <h1 style={styles.title}>Your Question</h1>
        <p style={styles.subtitle}>This page is private to the GAStreaming profile that submitted the question.</p>
      </section>

      {loading ? <div style={styles.empty}>Loading your response…</div> : null}
      {error ? (
        <div style={styles.error}>
          <div>{error}</div>
          {!getAuthHeader() ? <Link to="/signin" style={styles.inlineLink}>Sign in to open your response.</Link> : null}
        </div>
      ) : null}

      {!loading && !error && question ? (
        <article style={styles.card}>
          <div style={styles.cardTop}>
            <div>
              <div style={styles.eyebrow}>{question.categoryLabel || "QUESTION"}</div>
              <h2 style={styles.cardTitle}>{question.status === "answered" ? "GAStreaming answered you" : "Your question is being reviewed"}</h2>
            </div>
            <span style={question.status === "answered" ? styles.answeredPill : styles.openPill}>
              {String(question.status || "open").toUpperCase()}
            </span>
          </div>

          <div style={styles.block}>
            <div style={styles.label}>YOUR QUESTION</div>
            <div style={styles.body}>{question.message}</div>
            <div style={styles.time}>Submitted {formatTime(question.createdAt)}</div>
          </div>

          {question.answer ? (
            <div style={styles.answerBlock}>
              <div style={styles.label}>GAStreaming RESPONSE</div>
              <div style={styles.body}>{question.answer}</div>
              <div style={styles.time}>
                Answered {formatTime(question.answeredAt)}
                {question.answeredByUsername ? ` by ${question.answeredByUsername}` : ""}
              </div>
            </div>
          ) : (
            <div style={styles.waiting}>Your question has not been answered yet. You’ll receive another bell notification and email when the response is ready.</div>
          )}

          <div style={styles.actions}>
            <Link to="/contact" style={styles.primaryLink}>Ask Another Question</Link>
            <Link to="/me" style={styles.secondaryLink}>Back to Profile</Link>
          </div>
        </article>
      ) : null}
    </div>
  );
}

const styles = {
  page: { maxWidth: 980, margin: "0 auto", padding: "18px 16px 80px" },
  hero: { borderRadius: 24, padding: "28px clamp(18px,4vw,42px)", border: "1px solid rgba(221,53,65,.34)", background: "linear-gradient(135deg, rgba(91,8,16,.68), rgba(7,7,10,.92))", boxShadow: "0 24px 70px rgba(0,0,0,.44)" },
  eyebrow: { color: "#ff9da4", fontWeight: 900, letterSpacing: ".15em", fontSize: 12 },
  title: { margin: "8px 0 9px", color: "#fff", fontSize: "clamp(2.4rem,8vw,5rem)", lineHeight: .98 },
  subtitle: { margin: 0, color: "rgba(255,255,255,.76)", lineHeight: 1.6 },
  card: { marginTop: 18, borderRadius: 22, padding: "22px clamp(14px,3vw,28px)", border: "1px solid rgba(255,255,255,.12)", background: "rgba(5,5,8,.87)", boxShadow: "0 22px 60px rgba(0,0,0,.38)" },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap" },
  cardTitle: { margin: "6px 0 0", color: "#fff", fontSize: "clamp(1.8rem,4vw,3rem)" },
  answeredPill: { borderRadius: 999, padding: "8px 11px", color: "#98f0b5", background: "rgba(14,96,47,.34)", border: "1px solid rgba(67,217,121,.32)", fontWeight: 900, fontSize: 12 },
  openPill: { borderRadius: 999, padding: "8px 11px", color: "#ffd27e", background: "rgba(125,69,2,.42)", border: "1px solid rgba(255,181,65,.4)", fontWeight: 900, fontSize: 12 },
  block: { marginTop: 18, borderRadius: 16, padding: 16, background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.09)" },
  answerBlock: { marginTop: 14, borderRadius: 16, padding: 16, background: "rgba(23,111,55,.18)", border: "1px solid rgba(71,210,119,.25)" },
  label: { color: "rgba(255,255,255,.5)", fontWeight: 900, fontSize: 11, letterSpacing: ".1em" },
  body: { marginTop: 9, color: "#fff", lineHeight: 1.7, whiteSpace: "pre-wrap" },
  time: { marginTop: 12, color: "rgba(255,255,255,.45)", fontSize: 12 },
  waiting: { marginTop: 14, borderRadius: 14, padding: 14, color: "#ffd99a", background: "rgba(128,78,9,.22)", border: "1px solid rgba(255,184,70,.25)", lineHeight: 1.55 },
  actions: { marginTop: 18, display: "flex", flexWrap: "wrap", gap: 10 },
  primaryLink: { borderRadius: 999, padding: "11px 15px", textDecoration: "none", color: "#fff", fontWeight: 900, background: "linear-gradient(135deg,#da1724,#7e040c)", border: "1px solid rgba(255,82,82,.65)" },
  secondaryLink: { borderRadius: 999, padding: "11px 15px", textDecoration: "none", color: "#fff", fontWeight: 900, background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.15)" },
  empty: { marginTop: 18, borderRadius: 18, padding: 24, textAlign: "center", color: "rgba(255,255,255,.62)", border: "1px dashed rgba(255,255,255,.15)", background: "rgba(0,0,0,.34)" },
  error: { marginTop: 18, borderRadius: 14, padding: 14, color: "#ffb7b7", background: "rgba(128,13,22,.26)", border: "1px solid rgba(255,76,88,.3)", lineHeight: 1.55 },
  inlineLink: { display: "inline-flex", marginTop: 10, color: "#fff", fontWeight: 900 },
};
